import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { FOLLOW_UP_BUCKETS, type FollowUpBucket } from "@alm/domain";
import { BookUser, MapPin, Plus } from "@/lib/icons";
import { PageHeader } from "@/components/app/shell/page-header";
import { Button } from "@/components/ui/button";
import { NewSourceDialogs } from "@/routes/_authenticated/_crm/crm/prospecting/-components/new-source-dialogs";
import { FollowUpTiles } from "./-components/follow-up-tiles";
import { ReferralBoard } from "./-components/referral-board";
import { ReferralReport } from "./-components/referral-report";
import { ReferralTable } from "./-components/referral-table";
import { ReferralToolbar } from "./-components/referral-toolbar";
import { useReferralSources } from "./-components/use-referral-sources";

/** A reminder links straight to a slice of the queue: `?mine=true&due=overdue`. */
export const Route = createFileRoute("/_authenticated/_crm/crm/referral-sources/")({
  validateSearch: (search: Record<string, unknown>): { due?: FollowUpBucket; mine?: boolean } => ({
    due: FOLLOW_UP_BUCKETS.some((bucket) => bucket === search.due)
      ? (search.due as FollowUpBucket)
      : undefined,
    mine: search.mine === true || search.mine === "true" ? true : undefined,
  }),
  component: ReferralSources,
});

/** The referral sources as a list to work, the way leads are: who is due, who has gone quiet, who
 * owns whom. */
function ReferralSources() {
  const model = useReferralSources(Route.useSearch());
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Referral sources"
        count={model.list.isPending || model.list.isError ? undefined : model.total}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/crm/prospecting">
                <MapPin className="size-4" />
                Prospecting map
              </Link>
            </Button>
            {model.canWrite ? (
              <>
                <Button variant="outline" onClick={() => setImporting(true)}>
                  <BookUser className="size-4" />
                  From the directory
                </Button>
                <Button onClick={() => setAdding(true)}>
                  <Plus className="size-4" />
                  Add source
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <FollowUpTiles model={model} />
      <ReferralToolbar model={model} />
      {model.view === "report" ? (
        <ReferralReport facilityId={model.facilityId} onOpen={model.open} />
      ) : model.view === "board" ? (
        <ReferralBoard
          rows={model.rows}
          canWrite={model.canWrite}
          onOpen={model.open}
          onMove={model.moveStage}
        />
      ) : (
        <ReferralTable model={model} />
      )}
      <NewSourceDialogs
        facilityId={model.facilityId}
        adding={adding}
        importing={importing}
        onCloseAdd={() => setAdding(false)}
        onCloseImport={() => setImporting(false)}
        onOpen={model.open}
      />
    </div>
  );
}
